import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import AdminSidebar from "../../components/AdminSidebar";
import AdminService from "../../services/admin_service";
import QRCode from "qrcode";
import achalLogo from "../../assets/images/achal_logo.png";
import {
  FiSearch, FiPlus, FiTrash2, FiDownload, FiMapPin,
  FiRefreshCw, FiX, FiAlertCircle, FiCheckCircle,
  FiHash, FiCalendar, FiGrid, FiList, FiChevronRight,
  FiCopy, FiShield, FiZap, FiImage
} from "react-icons/fi";

/* ─────────────────────────────────────────
   CONSTANTS — DO NOT CHANGE BACKEND LOGIC
───────────────────────────────────────── */
const ACHAL_LOGO = achalLogo;

/* ─────────────────────────────────────────
   QR DATA URL GENERATOR
   STRICTLY uses location.qr_code_token only
───────────────────────────────────────── */
const generateQRDataUrl = (token, size = 300) =>
  QRCode.toDataURL(token, {
    width: size,
    margin: 1,
    color: { dark: "#0F172A", light: "#FFFFFF" },
    errorCorrectionLevel: "H",
  });

/* ─────────────────────────────────────────
   DRAW CARD ON CANVAS (shared by PNG + PDF)
───────────────────────────────────────── */
const drawCardOnCanvas = async (location, qrDataUrl) => {
  const W = 600;
  const H = 900;

  const canvas = document.createElement("canvas");
  canvas.width  = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  /* ── Background ── */
  ctx.fillStyle = "#FDFCF8";
  ctx.fillRect(0, 0, W, H);

  /* ── Subtle background texture dots ── */
  ctx.fillStyle = "rgba(212,175,55,0.04)";
  for (let x = 20; x < W; x += 28) {
    for (let y = 20; y < H; y += 28) {
      ctx.beginPath();
      ctx.arc(x, y, 1, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /* ── Outer border ── */
  ctx.strokeStyle = "#D4AF37";
  ctx.lineWidth = 2.5;
  roundRect(ctx, 10, 10, W - 20, H - 20, 26, false, true);

  /* ── Inner border ── */
  ctx.strokeStyle = "rgba(212,175,55,0.2)";
  ctx.lineWidth = 1;
  roundRect(ctx, 18, 18, W - 36, H - 36, 20, false, true);

  /* ── Top dark header band ── */
  const headerH = 220;
  const grad = ctx.createLinearGradient(0, 0, 0, headerH);
  grad.addColorStop(0, "#0A1628");
  grad.addColorStop(1, "#1E293B");
  ctx.fillStyle = grad;
  roundRect(ctx, 10, 10, W - 20, headerH, { tl: 23, tr: 23, br: 0, bl: 0 }, true, false);

  /* ── Gold accent line below header ── */
  const goldGrad = ctx.createLinearGradient(0, 0, W, 0);
  goldGrad.addColorStop(0, "transparent");
  goldGrad.addColorStop(0.15, "#D4AF37");
  goldGrad.addColorStop(0.85, "#D4AF37");
  goldGrad.addColorStop(1, "transparent");
  ctx.fillStyle = goldGrad;
  ctx.fillRect(10, 10 + headerH, W - 20, 3);

  /* ── Logo (real image, no AJ fallback text as primary) ── */
  const logoSize = 72;
  const logoX = W / 2 - logoSize / 2;
  const logoY = 36;

  // Gold outer ring
  ctx.strokeStyle = "#D4AF37";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.arc(W / 2, logoY + logoSize / 2, logoSize / 2 + 7, 0, Math.PI * 2);
  ctx.stroke();

  // White background circle behind logo
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath();
  ctx.arc(W / 2, logoY + logoSize / 2, logoSize / 2 + 2, 0, Math.PI * 2);
  ctx.fill();

  try {
    const logoImg = await loadImage(`${window.location.origin}${ACHAL_LOGO}`);
    ctx.save();
    ctx.beginPath();
    ctx.arc(W / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(logoImg, logoX, logoY, logoSize, logoSize);
    ctx.restore();
  } catch {
    // Elegant monogram fallback
    ctx.save();
    ctx.beginPath();
    ctx.arc(W / 2, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = "#D4AF37";
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = "#0F172A";
    ctx.font = "bold 28px Georgia, serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("AJ", W / 2, logoY + logoSize / 2);
  }

  /* ── Brand Name ── */
  ctx.fillStyle = "#D4AF37";
  ctx.font = "700 28px 'Georgia', serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("ACHAL JEWELS", W / 2, 158);

  /* ── Thin gold line under brand ── */
  ctx.strokeStyle = "rgba(212,175,55,0.35)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 70, 168);
  ctx.lineTo(W / 2 + 70, 168);
  ctx.stroke();

  ctx.fillStyle = "rgba(255,255,255,0.38)";
  ctx.font = "500 11px 'Arial', sans-serif";

  /* ── LOCATION LABEL ── */
  ctx.fillStyle = "#94A3B8";
  ctx.font = "700 10px 'Arial', sans-serif";
  ctx.textBaseline = "alphabetic";
  ctx.fillText("LOCATION", W / 2, 248);

  /* ── Decorative dots beside LOCATION label ── */
  ctx.fillStyle = "#D4AF37";
  ctx.beginPath(); ctx.arc(W / 2 - 58, 244, 2, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(W / 2 + 58, 244, 2, 0, Math.PI * 2); ctx.fill();

  /* ── Location Name ── */
  ctx.fillStyle = "#0F172A";
  ctx.font = "700 36px 'Georgia', serif";
  const maxW = W - 100;
  const lines = wrapText(ctx, location.location_name, maxW);
  lines.forEach((line, i) => {
    ctx.fillText(line, W / 2, 292 + i * 44);
  });
  const locTextBottom = 292 + lines.length * 44;

  /* ── Gold scan strip ── */
  const stripY = locTextBottom + 24;
  ctx.fillStyle = "#0A1628";
  roundRect(ctx, 70, stripY, W - 140, 42, 10, true, false);
  // Gold accent on strip
  ctx.fillStyle = "rgba(212,175,55,0.15)";
  roundRect(ctx, 70, stripY, W - 140, 42, 10, true, false);
  ctx.fillStyle = "#D4AF37";
  ctx.font = "800 11px 'Arial', sans-serif";
  ctx.fillText("● SCAN TO RAISE A TICKET ●", W / 2, stripY + 27);

  /* ── QR Code ── */
  const qrSize = 280;
  const qrX = (W - qrSize) / 2;
  const qrY = stripY + 68;

  // QR outer shadow/glow effect
  ctx.shadowColor = "rgba(212,175,55,0.12)";
  ctx.shadowBlur = 20;
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, qrX - 22, qrY - 22, qrSize + 44, qrSize + 44, 18, true, false);
  ctx.shadowBlur = 0;

  // QR border
  ctx.strokeStyle = "#E8EDF5";
  ctx.lineWidth = 1.5;
  roundRect(ctx, qrX - 22, qrY - 22, qrSize + 44, qrSize + 44, 18, false, true);

  // Gold corner marks
  const cm = 20;
  const cx0 = qrX - 22, cy0 = qrY - 22;
  const cx1 = qrX + qrSize + 22, cy1 = qrY + qrSize + 22;
  ctx.strokeStyle = "#D4AF37";
  ctx.lineWidth = 3;
  [[cx0, cy0, cm, 0, 0, cm], [cx1, cy0, -cm, 0, 0, cm], [cx0, cy1, cm, 0, 0, -cm], [cx1, cy1, -cm, 0, 0, -cm]].forEach(([x, y, dx1, dy1, dx2, dy2]) => {
    ctx.beginPath();
    ctx.moveTo(x + dx1, y);
    ctx.lineTo(x, y);
    ctx.lineTo(x, y + dy2 || y - Math.abs(dy2));
    // recalc
    ctx.stroke();
  });
  // Redo corners cleanly
  ctx.beginPath(); ctx.moveTo(cx0 + cm, cy0); ctx.lineTo(cx0, cy0); ctx.lineTo(cx0, cy0 + cm); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx1 - cm, cy0); ctx.lineTo(cx1, cy0); ctx.lineTo(cx1, cy0 + cm); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx0 + cm, cy1); ctx.lineTo(cx0, cy1); ctx.lineTo(cx0, cy1 - cm); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(cx1 - cm, cy1); ctx.lineTo(cx1, cy1); ctx.lineTo(cx1, cy1 - cm); ctx.stroke();

  const qrImg = await loadImage(qrDataUrl);
  ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);

  /* ── Token ID ── */
  const tokenY = qrY + qrSize + 60;
  ctx.fillStyle = "#94A3B8";
  ctx.font = "600 10px 'Arial', sans-serif";
  ctx.fillText("SYSTEM TOKEN", W / 2, tokenY);
  ctx.fillStyle = "#475569";
  ctx.font = "600 13px 'Courier New', monospace";
  ctx.fillText(location.qr_code_token, W / 2, tokenY + 24);

  /* ── Bottom divider ── */
  const divY = tokenY + 48;
  const divGrad = ctx.createLinearGradient(0, 0, W, 0);
  divGrad.addColorStop(0, "transparent");
  divGrad.addColorStop(0.2, "#E2E8F0");
  divGrad.addColorStop(0.8, "#E2E8F0");
  divGrad.addColorStop(1, "transparent");
  ctx.fillStyle = divGrad;
  ctx.fillRect(50, divY, W - 100, 1);

  return canvas;
};

/* ── helpers ── */
const loadImage = (src) =>
  new Promise((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = src;
  });

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(" ");
  const lines = [];
  let current = "";
  words.forEach(w => {
    const test = current ? `${current} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = test;
    }
  });
  if (current) lines.push(current);
  return lines;
};

const roundRect = (ctx, x, y, w, h, r, fill, stroke) => {
  let tl = r, tr = r, br = r, bl = r;
  if (typeof r === "object") { tl = r.tl || 0; tr = r.tr || 0; br = r.br || 0; bl = r.bl || 0; }
  ctx.beginPath();
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + w - tr, y); ctx.quadraticCurveTo(x + w, y, x + w, y + tr);
  ctx.lineTo(x + w, y + h - br); ctx.quadraticCurveTo(x + w, y + h, x + w - br, y + h);
  ctx.lineTo(x + bl, y + h); ctx.quadraticCurveTo(x, y + h, x, y + h - bl);
  ctx.lineTo(x, y + tl); ctx.quadraticCurveTo(x, y, x + tl, y);
  ctx.closePath();
  if (fill) ctx.fill();
  if (stroke) ctx.stroke();
};

/* ─────────────────────────────────────────
   DOWNLOAD PNG
───────────────────────────────────────── */
const downloadQRCardPNG = async (location) => {
  try {
    const deepLink = `https://api.ajplservices.tech/api/admin/scan/${location.qr_code_token}`;
    const qrDataUrl = await generateQRDataUrl(deepLink, 400);
    const canvas = await drawCardOnCanvas(location, qrDataUrl);
    const link = document.createElement("a");
    link.download = `QR_${location.location_name.replace(/\s+/g, "_")}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  } catch (err) {
    console.error("PNG download error:", err);
  }
};

/* ─────────────────────────────────────────
   DOWNLOAD PDF
───────────────────────────────────────── */
const downloadQRCardPDF = async (location) => {
  let qrDataUrl = "";
  try {
    const deepLink = `https://api.ajplservices.tech/api/admin/scan/${location.qr_code_token}`;
    qrDataUrl = await generateQRDataUrl(deepLink, 400);
  } catch (err) {
    console.error(err);
    return;
  }

  const origin = window.location.origin;

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8"/>
  <title>QR — ${location.location_name}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Sans:wght@400;600;700;800;900&family=DM+Mono:wght@500;600&display=swap');
    *{margin:0;padding:0;box-sizing:border-box;}
    html,body{
      width:100%;
      min-height:100vh;
      background:#E8ECF0;
      font-family:'DM Sans',sans-serif;
      display:flex;
      align-items:center;
      justify-content:center;
    }
    .page-wrap{
      width:100%;
      min-height:100vh;
      display:flex;
      align-items:center;
      justify-content:center;
      padding:20mm 0;
    }
    .page{
      width:85mm;
      background:#FDFCF8;
      position:relative;
      display:flex;
      flex-direction:column;
      overflow:hidden;
      border-radius:7mm;
      box-shadow:0 8mm 32mm rgba(0,0,0,0.18);
    }

    /* Outer gold border */
    .outer-border{
      position:absolute;
      inset:2mm;
      border:0.5mm solid #D4AF37;
      border-radius:6mm;
      pointer-events:none;
      z-index:10;
    }
    /* Inner subtle border */
    .inner-border{
      position:absolute;
      inset:3.5mm;
      border:0.2mm solid rgba(212,175,55,0.2);
      border-radius:5mm;
      pointer-events:none;
      z-index:9;
    }

    /* Corner accent blocks */
    .ca{position:absolute;width:8mm;height:8mm;z-index:11;}
    .ca-tl{top:1mm;left:1mm;border-top:0.8mm solid #D4AF37;border-left:0.8mm solid #D4AF37;border-radius:1mm 0 0 0;}
    .ca-tr{top:1mm;right:1mm;border-top:0.8mm solid #D4AF37;border-right:0.8mm solid #D4AF37;border-radius:0 1mm 0 0;}
    .ca-bl{bottom:1mm;left:1mm;border-bottom:0.8mm solid #D4AF37;border-left:0.8mm solid #D4AF37;border-radius:0 0 0 1mm;}
    .ca-br{bottom:1mm;right:1mm;border-bottom:0.8mm solid #D4AF37;border-right:0.8mm solid #D4AF37;border-radius:0 0 1mm 0;}

    /* Background dot pattern */
    .bg-pattern{
      position:absolute;
      inset:0;
      background-image:radial-gradient(circle, rgba(212,175,55,0.06) 1px, transparent 1px);
      background-size:5mm 5mm;
      pointer-events:none;
      z-index:0;
    }

    /* Header dark */
    .header{
      background:linear-gradient(160deg,#0A1628 0%,#1E293B 100%);
      padding:7mm 6mm 5mm;
      text-align:center;
      position:relative;
      z-index:1;
      border-radius:5.5mm 5.5mm 0 0;
    }
    .header::after{
      content:'';
      position:absolute;
      bottom:0;left:0;right:0;
      height:0.6mm;
      background:linear-gradient(90deg,transparent,#D4AF37 20%,#D4AF37 80%,transparent);
    }

    /* Logo */
    .logo-outer{
      width:16mm;height:16mm;
      border-radius:50%;
      border:0.6mm solid #D4AF37;
      margin:0 auto 2.5mm;
      display:flex;align-items:center;justify-content:center;
      position:relative;
    }
    .logo-inner{
      width:14.5mm;height:14.5mm;
      border-radius:50%;
      background:#FFFFFF;
      overflow:hidden;
      display:flex;align-items:center;justify-content:center;
    }
    .logo-img{width:100%;height:100%;object-fit:contain;}
    .logo-fb{font-size:5.5mm;font-weight:900;color:#0F172A;font-family:'Cormorant Garamond',serif;}

    .brand-name{
      font-family:'Cormorant Garamond',serif;
      font-size:6.5mm;
      font-weight:700;
      color:#D4AF37;
      letter-spacing:1.2mm;
      text-transform:uppercase;
    }
    .brand-rule{
      width:20mm;
      height:0.15mm;
      background:rgba(212,175,55,0.35);
      margin:1.5mm auto;
    }
    .brand-sub{
      font-size:2mm;
      color:rgba(255,255,255,0.38);
      letter-spacing:0.8mm;
      text-transform:uppercase;
    }

    /* Location */
    .loc-section{padding:4.5mm 7mm 3mm;text-align:center;position:relative;z-index:1;}
    .loc-label-row{display:flex;align-items:center;justify-content:center;gap:2mm;margin-bottom:1.5mm;}
    .loc-dot{width:1.5mm;height:1.5mm;border-radius:50%;background:#D4AF37;flex-shrink:0;}
    .loc-label{font-size:2mm;font-weight:800;color:#94A3B8;letter-spacing:1.2mm;text-transform:uppercase;}
    .loc-name{font-family:'Cormorant Garamond',serif;font-size:9mm;font-weight:700;color:#0F172A;line-height:1.15;}

    /* Scan strip */
    .scan-strip{
      margin:3mm 7mm 2mm;
      padding:2.5mm 4mm;
      background:#0A1628;
      border-radius:2.5mm;
      display:flex;align-items:center;justify-content:center;gap:2mm;
      position:relative;z-index:1;
    }
    .scan-dot{width:1.8mm;height:1.8mm;border-radius:50%;background:#D4AF37;flex-shrink:0;}
    .scan-txt{font-size:2.5mm;font-weight:900;color:#D4AF37;letter-spacing:0.7mm;text-transform:uppercase;}

    /* QR */
    .qr-wrap{padding:3mm 7mm 2mm;display:flex;justify-content:center;position:relative;z-index:1;}
    .qr-frame{
      border:0.4mm solid #E2E8F0;
      border-radius:4mm;
      padding:2.5mm;
      background:#fff;
      position:relative;
      box-shadow:0 1.5mm 8mm rgba(212,175,55,0.1), 0 0.5mm 3mm rgba(0,0,0,0.06);
    }
    .qr-img{width:42mm;height:42mm;display:block;}
    .qr-ca{position:absolute;width:4.5mm;height:4.5mm;border-color:#D4AF37;border-style:solid;border-width:0;}
    .qr-ca.tl{top:-0.5mm;left:-0.5mm;border-top-width:0.6mm;border-left-width:0.6mm;border-radius:0.5mm 0 0 0;}
    .qr-ca.tr{top:-0.5mm;right:-0.5mm;border-top-width:0.6mm;border-right-width:0.6mm;border-radius:0 0.5mm 0 0;}
    .qr-ca.bl{bottom:-0.5mm;left:-0.5mm;border-bottom-width:0.6mm;border-left-width:0.6mm;border-radius:0 0 0 0.5mm;}
    .qr-ca.br{bottom:-0.5mm;right:-0.5mm;border-bottom-width:0.6mm;border-right-width:0.6mm;border-radius:0 0 0.5mm 0;}

    /* Token */
    .token-section{text-align:center;padding:1.5mm 7mm 3mm;position:relative;z-index:1;}
    .token-label{font-size:1.8mm;color:#CBD5E1;text-transform:uppercase;letter-spacing:0.7mm;font-weight:700;}
    .token-val{font-family:'DM Mono',monospace;font-size:2.8mm;font-weight:600;color:#64748B;margin-top:0.8mm;}

    /* Footer */
    .footer{
      margin-top:auto;
      position:relative;z-index:1;
      padding:3mm 7mm 4mm;
      text-align:center;
    }
    .footer-rule{
      height:0.2mm;
      background:linear-gradient(90deg,transparent,#E2E8F0 20%,#E2E8F0 80%,transparent);
      margin-bottom:2.5mm;
    }
    .footer-by{font-size:1.8mm;color:#CBD5E1;letter-spacing:0.4mm;}
    .footer-brand{font-size:2.6mm;font-weight:700;color:#475569;margin-top:0.6mm;}
    .footer-brand span{color:#D4AF37;}

    @media print{
      html,body{
        width:100%;
        background:#E8ECF0;
      }
      .page-wrap{
        width:100%;
        min-height:100vh;
        display:flex;
        align-items:center;
        justify-content:center;
        padding:0;
      }
      .page{
        page-break-inside:avoid;
        margin:auto;
      }
      @page{
        margin:0;
        size:A4;
      }
    }
  </style>
</head>
<body>
<div class="page-wrap">
  <div class="page">
    <div class="bg-pattern"></div>
    <div class="outer-border"></div>
    <div class="inner-border"></div>
    <div class="ca ca-tl"></div><div class="ca ca-tr"></div>
    <div class="ca ca-bl"></div><div class="ca ca-br"></div>

    <div class="header">
      <div class="logo-outer">
        <div class="logo-inner">
          <img src="${ACHAL_LOGO}" class="logo-img" alt="AJ"
            onerror="this.style.display='none';this.parentNode.innerHTML='<span class=logo-fb>AJ</span>'"/>
        </div>
      </div>
      <div class="brand-name">Achal Jewels</div>
      <div class="brand-rule"></div>
    </div>

    <div class="loc-section">
      <div class="loc-label-row">
        <div class="loc-dot"></div>
        <div class="loc-label">Location</div>
        <div class="loc-dot"></div>
      </div>
      <div class="loc-name">${location.location_name}</div>
    </div>

    <div class="scan-strip">
      <div class="scan-dot"></div>
      <div class="scan-txt">Scan to Raise a Ticket</div>
      <div class="scan-dot"></div>
    </div>

    <div class="qr-wrap">
      <div class="qr-frame">
        <div class="qr-ca tl"></div><div class="qr-ca tr"></div>
        <div class="qr-ca bl"></div><div class="qr-ca br"></div>
        <img src="${qrDataUrl}" class="qr-img" alt="QR"/>
      </div>
    </div>

    <div class="token-section">
      <div class="token-label">System Token</div>
      <div class="token-val">${location.qr_code_token}</div>
    </div>
  </div>
</div>
<script>window.onload=function(){setTimeout(function(){window.print();},500);};</script>
</body>
</html>`;

  const win = window.open("", "_blank", "width=500,height=800");
  if (win) { win.document.write(html); win.document.close(); }
};

/* ─────────────────────────────────────────
   LOCATION CARD (page grid/list item)
   Preview feature completely removed
───────────────────────────────────────── */
const LocationCard = ({ location, viewMode, onDelete, onDownloadPDF, onDownloadPNG }) => {
  const [copied, setCopied] = useState(false);

  const copyToken = () => {
    navigator.clipboard.writeText(location.qr_code_token);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (viewMode === "list") {
    return (
      <div className="loc-row" style={styles.listRow}>
        <div style={styles.listQrMini}>
          <MiniQR
            token={`https://api.ajplservices.tech/api/admin/scan/${location.qr_code_token}`}
            locationName={location.location_name}
            locationToken={location.qr_code_token}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={styles.listLocName}>{location.location_name}</div>
          <div style={styles.listMeta}>
            <span style={styles.tokenChip}><FiHash size={10}/> {location.qr_code_token}</span>
            <span style={styles.dateMeta}><FiCalendar size={10}/> {new Date(location.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button onClick={copyToken} className="act-btn" style={styles.iconBtn} title="Copy Token">
            {copied ? <FiCheckCircle size={14} style={{ color: "#059669" }}/> : <FiCopy size={14}/>}
          </button>
          <button onClick={() => onDownloadPNG(location)} className="png-btn" style={styles.pngBtn} title="Download PNG">
            <FiImage size={13}/> PNG
          </button>
          <button onClick={() => onDownloadPDF(location)} className="dl-btn" style={styles.dlBtn} title="Download PDF">
            <FiDownload size={13}/> PDF
          </button>
          <button onClick={() => onDelete(location)} className="del-btn" style={styles.delBtn}>
            <FiTrash2 size={13}/>
          </button>
        </div>
      </div>
    );
  }

  // Grid card
  return (
    <div className="g-loc-card" style={styles.gridLocCard}>
      {/* Top gold accent */}
      <div style={styles.gridCardGoldTop}/>

      {/* Card content — compact summary */}
      <div style={styles.gridCardTop}>
        <div style={styles.gridLocIcon}>
          <FiMapPin size={18} style={{ color: "#D4AF37" }}/>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.gridLocName}>{location.location_name}</div>
          <div style={styles.gridLocDate}><FiCalendar size={9}/> {new Date(location.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
        </div>
      </div>

      {/* Mini QR preview — decorative only */}
      <div style={styles.gridQrSection}>
        <div style={styles.gridQrFrame}>
          <div style={{ ...styles.previewQrCorner, top: -2, left: -2, borderTopColor: "#D4AF37", borderLeftColor: "#D4AF37" }}/>
          <div style={{ ...styles.previewQrCorner, top: -2, right: -2, borderTopColor: "#D4AF37", borderRightColor: "#D4AF37" }}/>
          <div style={{ ...styles.previewQrCorner, bottom: -2, left: -2, borderBottomColor: "#D4AF37", borderLeftColor: "#D4AF37" }}/>
          <div style={{ ...styles.previewQrCorner, bottom: -2, right: -2, borderBottomColor: "#D4AF37", borderRightColor: "#D4AF37" }}/>
          <MiniQR
            token={location.qr_code_token}
            size={120}
            locationName={location.location_name}
            locationToken={location.qr_code_token}
          />
        </div>
      </div>

      {/* Token chip */}
      <div style={styles.gridTokenRow}>
        <span style={styles.gridTokenChip}>{location.qr_code_token}</span>
        <button onClick={copyToken} className="act-btn" style={styles.copyIconBtn}>
          {copied ? <FiCheckCircle size={12} style={{ color: "#059669" }}/> : <FiCopy size={12}/>}
        </button>
      </div>

      {/* Actions */}
      <div style={styles.gridCardActions}>
        <button onClick={() => onDownloadPNG(location)} className="png-btn" style={styles.pngBtnGrid}>
          <FiImage size={12}/> PNG
        </button>
        <button onClick={() => onDownloadPDF(location)} className="dl-btn" style={styles.dlBtn}>
          <FiDownload size={12}/> PDF
        </button>
        <button onClick={() => onDelete(location)} className="del-btn" style={styles.delBtn}>
          <FiTrash2 size={13}/>
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   MINI QR — decorative, blurred, non-scannable
   Shows blurred QR with center overlay label
───────────────────────────────────────── */
const MiniQR = ({ token, size = 60, locationName = "", locationToken = "" }) => {
  const [url, setUrl] = useState("");

  useEffect(() => {
    generateQRDataUrl(token, size * 2).then(setUrl).catch(console.error);
  }, [token, size]);

  // Build short display label: "Location Name LOC-TOKEN"
  const shortName = locationName.length > 14
    ? locationName.slice(0, 14).trim() + "…"
    : locationName;
  const shortToken = locationToken ? locationToken.slice(0, 12) : "";
  const overlayLabel = shortName || shortToken;

  return (
    <div style={{
      width: size,
      height: size,
      position: "relative",
      borderRadius: "8px",
      overflow: "hidden",
      background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
      boxShadow: "inset 0 1px 3px rgba(0,0,0,0.08)",
    }}>
      {/* Blurred QR background */}
      {url ? (
        <img
          src={url}
          alt=""
          aria-hidden="true"
          style={{
            width: "100%",
            height: "100%",
            display: "block",
            filter: "blur(4.5px) saturate(0.5) opacity(0.55)",
            transform: "scale(1.05)",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      ) : (
        <div style={{
          width: "100%",
          height: "100%",
          background: "repeating-linear-gradient(45deg, #E8EDF5 0px, #E8EDF5 2px, #F1F5F9 2px, #F1F5F9 8px)",
          opacity: 0.5,
        }}/>
      )}

      {/* Glassmorphism overlay */}
      <div style={{
        position: "absolute",
        inset: 0,
        background: "linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(248,250,252,0.68) 100%)",
        backdropFilter: "blur(2px)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "3px",
      }}>
        {/* Gold QR icon hint */}
        <div style={{
          width: size * 0.32,
          height: size * 0.32,
          border: "1.5px solid #D4AF37",
          borderRadius: "3px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          background: "rgba(255,255,255,0.9)",
          boxShadow: "0 1px 4px rgba(212,175,55,0.2)",
        }}>
          {/* Mini QR pattern hint */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: "1px", padding: "2px" }}>
            {[1,1,0,1,0,1,0,1,1].map((v, i) => (
              <div key={i} style={{
                width: size * 0.06,
                height: size * 0.06,
                borderRadius: "0.5px",
                background: v ? "#0F172A" : "transparent",
              }}/>
            ))}
          </div>
        </div>

        {/* Location name label */}
        {overlayLabel && (
          <div style={{
            fontSize: Math.max(7, size * 0.085) + "px",
            fontWeight: 700,
            color: "#1E293B",
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: "0.2px",
            textAlign: "center",
            lineHeight: 1.2,
            maxWidth: size - 8,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            padding: "0 4px",
          }}>
            {overlayLabel}
          </div>
        )}
        {shortToken && shortToken !== overlayLabel && (
          <div style={{
            fontSize: Math.max(6, size * 0.07) + "px",
            fontWeight: 600,
            color: "#94A3B8",
            fontFamily: "'DM Mono', monospace",
            letterSpacing: "0.3px",
            textAlign: "center",
            maxWidth: size - 8,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {shortToken}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────
   MAIN PAGE
───────────────────────────────────────── */
const LocationQR = () => {
  const [locations,     setLocations]     = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [addLoading,    setAddLoading]    = useState(false);
  const [searchTerm,    setSearchTerm]    = useState("");
  const [viewMode,      setViewMode]      = useState("grid");
  const [showAddModal,  setShowAddModal]  = useState(false);
  const [newLocName,    setNewLocName]    = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [toast,         setToast]         = useState(null);
  const inputRef = useRef();

  const showToast = useCallback((type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminService.getAllLocations();
      if (res.success) setLocations(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);
  useEffect(() => { if (showAddModal) setTimeout(() => inputRef.current?.focus(), 120); }, [showAddModal]);

  const filteredLocations = useMemo(() => {
    if (!searchTerm.trim()) return locations;
    const s = searchTerm.toLowerCase();
    return locations.filter(l =>
      l.location_name?.toLowerCase().includes(s) ||
      l.qr_code_token?.toLowerCase().includes(s)
    );
  }, [locations, searchTerm]);

  const handleAdd = async () => {
    if (!newLocName.trim()) return;
    setAddLoading(true);
    try {
      const res = await AdminService.addLocation({ location_name: newLocName.trim() });
      if (res.success) {
        showToast("success", `"${newLocName.trim()}" registered successfully`);
        setNewLocName(""); setShowAddModal(false); fetchLocations();
      } else { showToast("error", res.message || "Failed to add location"); }
    } catch { showToast("error", "Failed to add location. Try again."); }
    finally { setAddLoading(false); }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const res = await AdminService.deleteLocation(deleteConfirm.id);
      if (res.success) {
        setLocations(prev => prev.filter(l => l.id !== deleteConfirm.id));
        showToast("success", "Location deleted successfully");
        setDeleteConfirm(null);
      }
    } catch { showToast("error", "Delete failed. Try again."); }
    finally { setDeleting(false); }
  };

  if (loading) return (
    <div style={styles.loaderPage}>
      <style>{CSS_ANIM}</style>
      <div style={styles.spinner}/>
      <div style={styles.loaderBrand}>ACHAL JEWELS</div>
      <div style={styles.loaderSub}>Loading Location Hub...</div>
      <div style={{ display: "flex", gap: "6px", marginTop: "18px" }}>
        {[0,1,2].map(i => <div key={i} style={{ ...styles.dot, animationDelay: `${i*0.2}s` }}/>)}
      </div>
    </div>
  );

  return (
    <>
      <style>{CSS_ANIM + CSS_HOVER}</style>

      {/* Toast */}
      {toast && (
        <div style={styles.toastWrap}>
          <div style={{
            ...styles.toast,
            background: toast.type === "success" ? "#ECFDF5" : "#FEF2F2",
            borderColor: toast.type === "success" ? "#A7F3D0" : "#FECACA",
            color:       toast.type === "success" ? "#059669" : "#DC2626",
          }}>
            {toast.type === "success" ? <FiCheckCircle size={15}/> : <FiAlertCircle size={15}/>}
            <span style={{ fontWeight: 700, fontSize: "13px" }}>{toast.text}</span>
          </div>
        </div>
      )}

      <div style={styles.layout}>
        <AdminSidebar/>
        <div style={styles.main}>

          {/* HEADER */}
          <div style={styles.header}>
            <div>
              <div style={styles.breadcrumb}>
                <span style={{ color: "#D4AF37", fontWeight: 800 }}>Admin</span>
                <FiChevronRight size={12} style={{ color: "#CBD5E1" }}/>
                <span>Location &amp; QR Hub</span>
              </div>
              <h1 style={styles.title}>Location <span style={{ color: "#D4AF37" }}>&amp; QR</span> Hub</h1>
              <p style={styles.subtitle}>Register physical locations and generate enterprise-grade QR access cards</p>
            </div>
            <div style={styles.headerRight}>
              <button onClick={fetchLocations} className="r-btn" style={styles.refreshBtn}>
                <FiRefreshCw size={14}/> Sync
              </button>
              <button onClick={() => setShowAddModal(true)} style={styles.addBtn}>
                <FiPlus size={15}/> Add Location
              </button>
            </div>
          </div>

          {/* STATS */}
          <div style={styles.statsRow}>
            {[
              { icon: <FiMapPin size={18}/>, iconBg: "#FEF3C7", iconColor: "#D97706", num: locations.length, lab: "Total Locations" },
              { icon: <FiZap size={18}/>, iconBg: "#ECFDF5", iconColor: "#059669", num: locations.length, lab: "Active QR Codes" },
              { icon: <FiShield size={18}/>, iconBg: "#EFF6FF", iconColor: "#2563EB", num: filteredLocations.length, lab: "Showing" },
            ].map((s, i) => (
              <div key={i} style={styles.statCard}>
                <div style={{ ...styles.statIcon, background: s.iconBg, color: s.iconColor }}>{s.icon}</div>
                <div>
                  <div style={styles.statNum}>{s.num}</div>
                  <div style={styles.statLab}>{s.lab}</div>
                </div>
              </div>
            ))}
          </div>

          {/* CONTROLS */}
          <div style={styles.controlRow}>
            <div style={styles.searchBox}>
              <FiSearch style={styles.searchIcon}/>
              <input
                placeholder="Search location name or token ID..."
                style={styles.searchInput}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} style={styles.clearSearch}><FiX size={12}/></button>
              )}
            </div>
            <div style={styles.viewToggle}>
              <button
                onClick={() => setViewMode("grid")}
                style={{ ...styles.toggleBtn, background: viewMode === "grid" ? "#1E293B" : "transparent", color: viewMode === "grid" ? "#FFF" : "#94A3B8" }}
              ><FiGrid size={14}/></button>
              <button
                onClick={() => setViewMode("list")}
                style={{ ...styles.toggleBtn, background: viewMode === "list" ? "#1E293B" : "transparent", color: viewMode === "list" ? "#FFF" : "#94A3B8" }}
              ><FiList size={14}/></button>
            </div>
            <div style={styles.resultCount}>
              <b style={{ color: "#0F172A" }}>{filteredLocations.length}</b>
              <span style={{ color: "#94A3B8" }}> / {locations.length} locations</span>
            </div>
          </div>

          {/* EMPTY */}
          {filteredLocations.length === 0 && (
            <div style={styles.emptyState}>
              <div style={styles.emptyIconBox}><FiMapPin size={34} style={{ color: "#D4AF37" }}/></div>
              <div style={styles.emptyTitle}>{searchTerm ? "No locations match your search" : "No locations registered yet"}</div>
              <div style={styles.emptySub}>{searchTerm ? "Try a different search term" : "Add your first location to generate a QR access card"}</div>
              {!searchTerm && (
                <button onClick={() => setShowAddModal(true)} style={{ ...styles.addBtn, marginTop: "20px" }}>
                  <FiPlus size={14}/> Add First Location
                </button>
              )}
            </div>
          )}

          {/* GRID */}
          {viewMode === "grid" && filteredLocations.length > 0 && (
            <div style={styles.gridWrap}>
              {filteredLocations.map(loc => (
                <LocationCard
                  key={loc.id} location={loc} viewMode="grid"
                  onDelete={setDeleteConfirm}
                  onDownloadPDF={downloadQRCardPDF}
                  onDownloadPNG={downloadQRCardPNG}
                />
              ))}
            </div>
          )}

          {/* LIST */}
          {viewMode === "list" && filteredLocations.length > 0 && (
            <div style={styles.listWrap}>
              {filteredLocations.map(loc => (
                <LocationCard
                  key={loc.id} location={loc} viewMode="list"
                  onDelete={setDeleteConfirm}
                  onDownloadPDF={downloadQRCardPDF}
                  onDownloadPNG={downloadQRCardPNG}
                />
              ))}
            </div>
          )}

        </div>
      </div>

      {/* ADD MODAL */}
      {showAddModal && (
        <div style={styles.modalOverlay} onClick={() => !addLoading && setShowAddModal(false)}>
          <div style={styles.modalBox} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalEyebrow}><FiMapPin size={11}/> New Location</div>
                <div style={styles.modalTitle}>Register Location</div>
              </div>
              <button onClick={() => setShowAddModal(false)} style={styles.modalClose}><FiX size={18}/></button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.modalNote}>
                A unique QR token will be auto-generated. Print the QR card and place it at the physical location so employees can scan and raise tickets instantly.
              </div>
              <label style={styles.modalLabel}>Location Name</label>
              <input
                ref={inputRef}
                type="text"
                placeholder="e.g. Reception Desk · Floor 3 · Cabin 12"
                style={styles.modalInput}
                value={newLocName}
                onChange={e => setNewLocName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !addLoading && newLocName.trim() && handleAdd()}
                maxLength={80}
              />
              <div style={{ fontSize: "11px", color: "#94A3B8", marginTop: "6px" }}>{newLocName.length}/80</div>
            </div>
            <div style={styles.modalFooter}>
              <button onClick={() => setShowAddModal(false)} style={styles.modalCancelBtn} disabled={addLoading}>Cancel</button>
              <button
                onClick={handleAdd}
                disabled={!newLocName.trim() || addLoading}
                style={{ ...styles.modalSaveBtn, opacity: newLocName.trim() && !addLoading ? 1 : 0.5, cursor: newLocName.trim() && !addLoading ? "pointer" : "not-allowed" }}
              >
                {addLoading ? <><FiRefreshCw size={13}/> Generating...</> : <><FiPlus size={13}/> Register &amp; Generate QR</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {deleteConfirm && (
        <div style={styles.modalOverlay} onClick={() => !deleting && setDeleteConfirm(null)}>
          <div style={{ ...styles.modalBox, maxWidth: "420px" }} onClick={e => e.stopPropagation()}>
            <div style={styles.delIconWrap}>
              <div style={styles.delIconCircle}><FiTrash2 size={22} color="#FFF"/></div>
            </div>
            <div style={{ textAlign: "center", padding: "0 28px 28px" }}>
              <div style={styles.delTitle}>Delete Location?</div>
              <div style={styles.delDesc}>
                Permanently removes <strong>"{deleteConfirm.location_name}"</strong> and token <strong>{deleteConfirm.qr_code_token}</strong>. Printed QR cards for this location will stop working.
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "22px" }}>
                <button onClick={() => setDeleteConfirm(null)} style={styles.modalCancelBtn} disabled={deleting}>Cancel</button>
                <button
                  onClick={handleDelete}
                  style={{ ...styles.delConfirmBtn, opacity: deleting ? 0.7 : 1 }}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : <><FiTrash2 size={13}/> Delete</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/* ─────────────────────────────────────────
   CSS
───────────────────────────────────────── */
const CSS_ANIM = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Sans:wght@400;500;600;700;800;900&family=DM+Mono:wght@500;600&display=swap');
  @keyframes spin    { to { transform: rotate(360deg); } }
  @keyframes fadeUp  { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
  @keyframes pulse   { 0%,100%{ opacity:1; } 50%{ opacity:0.3; } }
  @keyframes popIn   { from { opacity:0; transform:scale(0.93) translateY(10px); } to { opacity:1; transform:scale(1) translateY(0); } }
  @keyframes slideDown { from { opacity:0; transform:translateY(-14px); } to { opacity:1; transform:translateY(0); } }
`;
const CSS_HOVER = `
  .r-btn:hover { background: #D4AF37 !important; color: #000 !important; border-color: #D4AF37 !important; }
  .g-loc-card  { transition: transform 0.24s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.24s; }
  .g-loc-card:hover { transform: translateY(-6px); box-shadow: 0 28px 56px rgba(0,0,0,0.14) !important; }
  .loc-row:hover { background: #FAFBFF !important; }
  .act-btn:hover { background: #F1F5F9 !important; color: #1E293B !important; }
  .png-btn:hover  { background: #2563EB !important; color: #FFF !important; border-color: #2563EB !important; }
  .dl-btn:hover   { background: #D4AF37 !important; color: #000 !important; border-color: #D4AF37 !important; }
  .del-btn:hover  { background: #DC2626 !important; color: #FFF !important; border-color: #DC2626 !important; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: #CBD5E1; border-radius: 99px; }
`;

/* ─────────────────────────────────────────
   STYLES
───────────────────────────────────────── */
const styles = {
  layout: { display: "flex", background: "#F0F2F5", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" },
  main:   { marginLeft: "260px", padding: "32px 40px", flex: 1, overflowX: "hidden" },

  header:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "24px" },
  breadcrumb: { display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#94A3B8", fontWeight: 600, marginBottom: "8px" },
  title:      { margin: 0, fontSize: "29px", fontWeight: 900, color: "#0F172A", letterSpacing: "-0.8px" },
  subtitle:   { margin: "4px 0 0", fontSize: "13px", color: "#94A3B8", fontWeight: 500 },
  headerRight:{ display: "flex", gap: "12px", alignItems: "center" },
  refreshBtn: { display: "flex", alignItems: "center", gap: "7px", padding: "11px 18px", borderRadius: "12px", border: "1.5px solid #E2E8F0", background: "#FFF", fontWeight: 700, cursor: "pointer", fontSize: "13px", color: "#475569", transition: "all 0.2s" },
  addBtn:     { display: "flex", alignItems: "center", gap: "7px", padding: "12px 22px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#D4AF37,#F0CC5F)", color: "#0F172A", fontWeight: 800, cursor: "pointer", fontSize: "13px", boxShadow: "0 4px 16px rgba(212,175,55,0.35)", transition: "all 0.2s" },

  statsRow: { display: "flex", gap: "14px", marginBottom: "22px" },
  statCard: { background: "#FFF", borderRadius: "16px", padding: "16px 20px", border: "1.5px solid #F1F5F9", display: "flex", alignItems: "center", gap: "14px", flex: 1, boxShadow: "0 2px 8px rgba(0,0,0,0.03)" },
  statIcon: { width: "44px", height: "44px", borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  statNum:  { fontSize: "24px", fontWeight: 900, color: "#0F172A" },
  statLab:  { fontSize: "11px", color: "#94A3B8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginTop: "2px" },

  controlRow:  { display: "flex", gap: "12px", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" },
  searchBox:   { position: "relative", flex: 1, minWidth: "240px", maxWidth: "440px" },
  searchIcon:  { position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94A3B8" },
  searchInput: { width: "100%", padding: "12px 36px 12px 42px", borderRadius: "12px", border: "1.5px solid #E2E8F0", outline: "none", background: "#FFF", fontSize: "13px", fontWeight: 500, color: "#1E293B", boxSizing: "border-box" },
  clearSearch: { position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", background: "#F1F5F9", border: "none", borderRadius: "50%", width: "22px", height: "22px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B" },
  viewToggle:  { display: "flex", background: "#E8ECF0", borderRadius: "10px", padding: "3px", gap: "2px" },
  toggleBtn:   { border: "none", padding: "8px 12px", borderRadius: "8px", cursor: "pointer", display: "flex", alignItems: "center", transition: "all 0.2s" },
  resultCount: { fontSize: "13px", marginLeft: "auto" },

  emptyState:  { textAlign: "center", padding: "80px 20px", display: "flex", flexDirection: "column", alignItems: "center" },
  emptyIconBox:{ width: "80px", height: "80px", borderRadius: "24px", background: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" },
  emptyTitle:  { fontSize: "18px", fontWeight: 800, color: "#1E293B", marginBottom: "8px" },
  emptySub:    { fontSize: "13px", color: "#94A3B8", maxWidth: "360px", lineHeight: "1.65" },

  /* ── Grid layout ── */
  gridWrap: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "20px" },

  gridLocCard:    { background: "#FFF", borderRadius: "22px", border: "1.5px solid #F1F5F9", overflow: "hidden", boxShadow: "0 4px 18px rgba(0,0,0,0.06)", animation: "fadeUp 0.3s ease both", display: "flex", flexDirection: "column" },
  gridCardGoldTop:{ height: "4px", background: "linear-gradient(90deg,#D4AF37,#F0CC5F,#D4AF37)", flexShrink: 0 },
  gridCardTop:    { display: "flex", alignItems: "flex-start", gap: "12px", padding: "18px 18px 10px" },
  gridLocIcon:    { width: "42px", height: "42px", borderRadius: "13px", background: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  gridLocName:    { fontFamily: "'Cormorant Garamond', serif", fontSize: "17px", fontWeight: 700, color: "#0F172A", lineHeight: "1.25" },
  gridLocDate:    { display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", color: "#CBD5E1", fontWeight: 600, marginTop: "4px" },
  gridQrSection:  { display: "flex", justifyContent: "center", padding: "8px 18px" },
  gridQrFrame:    { position: "relative", padding: "10px", background: "#F8FAFC", borderRadius: "12px", border: "1.5px solid #E8EDF5", display: "inline-block" },
  gridTokenRow:   { display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", padding: "6px 18px" },
  gridTokenChip:  { fontFamily: "'DM Mono', monospace", fontSize: "10px", fontWeight: 600, color: "#64748B", background: "#F1F5F9", padding: "4px 10px", borderRadius: "6px" },
  copyIconBtn:    { background: "none", border: "none", cursor: "pointer", color: "#94A3B8", display: "flex", alignItems: "center", padding: "4px", borderRadius: "6px" },
  gridCardActions:{ padding: "12px 14px", borderTop: "1.5px solid #F8FAFC", display: "flex", gap: "6px" },
  pngBtnGrid:     { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", padding: "9px 6px", borderRadius: "9px", border: "1.5px solid #BFDBFE", background: "#EFF6FF", color: "#2563EB", fontWeight: 700, cursor: "pointer", fontSize: "11px", transition: "all 0.18s" },
  pngBtn:         { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", padding: "9px 6px", borderRadius: "9px", border: "1.5px solid #BFDBFE", background: "#EFF6FF", color: "#2563EB", fontWeight: 700, cursor: "pointer", fontSize: "11px", transition: "all 0.18s" },
  dlBtn:          { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "5px", padding: "9px 6px", borderRadius: "9px", border: "1.5px solid #D4AF37", background: "#FFFBEB", color: "#9A7B10", fontWeight: 700, cursor: "pointer", fontSize: "11px", transition: "all 0.18s" },
  delBtn:         { display: "flex", alignItems: "center", justifyContent: "center", padding: "9px 10px", borderRadius: "9px", border: "1.5px solid #FECACA", background: "#FEF2F2", color: "#EF4444", cursor: "pointer", transition: "all 0.18s" },

  previewQrCorner:{ position: "absolute", width: "14px", height: "14px", borderColor: "transparent", borderStyle: "solid", borderWidth: "2.5px" },

  /* ── List layout ── */
  listWrap: { background: "#FFF", borderRadius: "20px", border: "1.5px solid #F1F5F9", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)" },
  listRow:  { display: "flex", alignItems: "center", gap: "16px", padding: "16px 20px", borderBottom: "1.5px solid #F8FAFC", background: "#FFF", transition: "background 0.18s", position: "relative" },
  listQrMini:{ width: "60px", height: "60px", borderRadius: "10px", border: "1.5px solid #E8EDF5", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" },
  listLocName:{ fontFamily: "'Cormorant Garamond', serif", fontSize: "18px", fontWeight: 700, color: "#0F172A" },
  listMeta:   { display: "flex", alignItems: "center", gap: "12px", marginTop: "4px" },
  tokenChip:  { display: "flex", alignItems: "center", gap: "4px", fontFamily: "'DM Mono', monospace", fontSize: "11px", color: "#64748B", background: "#F1F5F9", padding: "3px 9px", borderRadius: "6px" },
  dateMeta:   { display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: "#94A3B8", fontWeight: 600 },
  iconBtn:    { background: "#F8FAFC", border: "1.5px solid #E2E8F0", color: "#64748B", width: "36px", height: "36px", borderRadius: "10px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.18s" },

  /* Modals */
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(8,14,30,0.5)", zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "center", backdropFilter: "blur(8px)" },
  modalBox:     { background: "#FFF", borderRadius: "24px", width: "480px", maxWidth: "94vw", boxShadow: "0 30px 80px rgba(0,0,0,0.22)", animation: "popIn 0.28s cubic-bezier(0.4,0,0.2,1)", overflow: "hidden" },
  modalHeader:  { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "24px 26px 0" },
  modalEyebrow: { display: "flex", alignItems: "center", gap: "6px", fontSize: "10px", color: "#D4AF37", fontWeight: 900, textTransform: "uppercase", letterSpacing: "1.5px", marginBottom: "5px" },
  modalTitle:   { fontSize: "20px", fontWeight: 900, color: "#0F172A" },
  modalClose:   { background: "#F1F5F9", border: "none", width: "38px", height: "38px", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B", flexShrink: 0 },
  modalBody:    { padding: "20px 26px" },
  modalNote:    { background: "#F8FAFC", border: "1.5px solid #E2E8F0", borderRadius: "12px", padding: "12px 14px", fontSize: "12px", color: "#475569", lineHeight: "1.6", marginBottom: "18px" },
  modalLabel:   { display: "block", fontSize: "10px", fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: "8px" },
  modalInput:   { width: "100%", padding: "13px 16px", borderRadius: "12px", border: "1.5px solid #E2E8F0", outline: "none", fontSize: "14px", fontWeight: 600, color: "#1E293B", boxSizing: "border-box", transition: "border-color 0.2s" },
  modalFooter:  { display: "flex", gap: "10px", padding: "0 26px 26px", marginTop: "20px" },
  modalCancelBtn:{ flex: 1, padding: "13px", borderRadius: "12px", border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontWeight: 700, cursor: "pointer", fontSize: "13px", color: "#475569" },
  modalSaveBtn:  { flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "13px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#D4AF37,#F0CC5F)", color: "#0F172A", fontWeight: 800, fontSize: "13px", boxShadow: "0 4px 16px rgba(212,175,55,0.3)", transition: "opacity 0.2s" },

  delIconWrap:    { display: "flex", justifyContent: "center", padding: "28px 0 16px" },
  delIconCircle:  { width: "60px", height: "60px", borderRadius: "18px", background: "linear-gradient(135deg,#EF4444,#DC2626)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 24px rgba(220,38,38,0.3)" },
  delTitle:       { fontSize: "20px", fontWeight: 900, color: "#0F172A", marginBottom: "10px" },
  delDesc:        { fontSize: "13px", color: "#64748B", lineHeight: "1.7" },
  delConfirmBtn:  { flex: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "13px", borderRadius: "12px", border: "none", background: "linear-gradient(135deg,#EF4444,#DC2626)", color: "#FFF", fontWeight: 800, cursor: "pointer", fontSize: "13px", boxShadow: "0 4px 16px rgba(220,38,38,0.3)", transition: "opacity 0.2s" },

  toastWrap: { position: "fixed", top: "24px", right: "24px", zIndex: 9999, animation: "slideDown 0.3s ease" },
  toast:     { display: "flex", alignItems: "center", gap: "10px", padding: "13px 18px", borderRadius: "14px", border: "1.5px solid", boxShadow: "0 10px 30px rgba(0,0,0,0.12)", minWidth: "280px" },

  loaderPage:  { height: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", background: "#F0F2F5", fontFamily: "'DM Sans', sans-serif" },
  spinner:     { width: "44px", height: "44px", border: "5px solid #E2E8F0", borderTopColor: "#D4AF37", borderRadius: "50%", animation: "spin 0.8s linear infinite" },
  loaderBrand: { marginTop: "20px", fontSize: "16px", fontWeight: 900, color: "#1E293B", letterSpacing: "4px" },
  loaderSub:   { color: "#94A3B8", fontSize: "12px", marginTop: "5px" },
  dot:         { width: "7px", height: "7px", borderRadius: "50%", background: "#D4AF37", animation: "pulse 1.2s ease infinite" },
};

export default LocationQR;